import type { Vec2 } from '../../geo/types';

/** Exact Float32 coordinate keys in bounded storage. Linear probing checks both full keys, so hash
 * collisions never merge different heights. The pure relief field can be resampled after eviction. */
export function cachedHeight(sample: (p: Vec2) => number): (p: Vec2) => number {
  const capacity = 16_384,
    mask = capacity - 1;
  const used = new Uint8Array(capacity);
  const xs = new Uint32Array(capacity),
    ys = new Uint32Array(capacity);
  const values = new Float64Array(capacity);
  const coordinate = new Float32Array(2),
    bits = new Uint32Array(coordinate.buffer);
  let count = 0;
  return (p) => {
    // Canonicalize signed zero as Map does; rounding matches the stored mesh coordinates.
    coordinate[0] = p[0];
    coordinate[1] = p[1];
    const x = coordinate[0] === 0 ? 0 : bits[0]!,
      y = coordinate[1] === 0 ? 0 : bits[1]!;
    const key = Math.imul(x, 0x45d9f3b) ^ Math.imul(y, 0x119de1f3);
    const bucket = (key ^ (key >>> 16)) & mask;
    let slot = bucket;
    while (used[slot]) {
      if (xs[slot] === x && ys[slot] === y) return values[slot]!;
      slot = (slot + 1) & mask;
    }
    const height = sample([coordinate[0], coordinate[1]]);
    if (count >= capacity / 2) {
      used.fill(0);
      count = 0;
      slot = bucket;
    }
    used[slot] = 1;
    xs[slot] = x;
    ys[slot] = y;
    values[slot] = height;
    count++;
    return height;
  };
}
